"use client";
import SectionWrapper, { SectionWrapperProps } from "@cc/section-wrapper";
import {
  MotionImage,
  MotionImageProps,
  MotionTypography,
  MotionTypographyProps,
} from "@cc/motion-components";
import { H6_2 } from "@theme/components/typography.fontvariant";
import { StaticImageData } from "next/image";
import AnimatedButton, { AnimatedButtonProps } from "@cc/animated-button";
import { Stack, StackProps } from "@mui/material";
import { deepmerge } from "@mui/utils";

export interface IntroSectionProps
  extends Omit<SectionWrapperProps, "children"> {
  title: string;
  TitleTypographyProps?: Omit<MotionTypographyProps, "children">;
  banner: StaticImageData | string;
  BannerProps?: Omit<MotionImageProps, "children">;
  description?: string;
  DescriptionTypographyProps?: Omit<MotionTypographyProps, "children">;
  InfoWrapperProps?: Omit<StackProps, "children">;
  ButtonProps?: AnimatedButtonProps;
}

const IntroSection = (props: IntroSectionProps) => {
  const {
    title,
    TitleTypographyProps,
    banner,
    BannerProps,
    description,
    DescriptionTypographyProps,
    InfoWrapperProps,
    ButtonProps,
    ...restSectionWrapperProps
  } = props;
  const { SectionHeaderProps, ...restSectionProps } = restSectionWrapperProps;
  return (
    <SectionWrapper
      color={"primary.main"}
      SectionHeaderProps={{
        title: title,
        TitleTypographyProps,
        ...SectionHeaderProps,
      }}
      direction={{ xs: "column", md_lg: "row" }}
      columnGap={10}
      rowGap={3}
      justifyContent={"space-between"}
      alignItems={"center"}
      {...restSectionProps}
    >
      <MotionImage
        src={banner}
        {...deepmerge(defaultImageProps, BannerProps)}
      />
      <Stack
        rowGap={{ xs: 4, md: 5 }}
        width={{ xs: "100%", md_lg: "50%" }}
        maxWidth={{md_lg:612}}
        {...InfoWrapperProps}
      >
        {description && (
          <MotionTypography
            component={"p"}
            fontSize={H6_2}
            textAlign={{ xs: "center", md_lg: "left" }}
            {...DescriptionTypographyProps}
          >
            {description}
          </MotionTypography>
        )}
        {ButtonProps && ButtonProps.href && ButtonProps.children && (
          <AnimatedButton
            variant={"contained"}
            color={"primary"}
            {...ButtonProps}
          />
        )}
      </Stack>
    </SectionWrapper>
  );
};

export default IntroSection;

const defaultImageProps: Omit<MotionImageProps, "src"> = {
  alt: "into-image",
  width: 840,
  height: 720,
  sx: {
    width: { xs: "100%", md_lg: "50%" },
    maxWidth: {md_lg:599},
    objectFit: "cover",
    aspectRatio: {md_lg:"4/2.556"},
  },
};
